import React, { useState } from 'react'

import { Button, Avatar } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, string } from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import { useUserData } from './Hooks/useUserData'
import { useUserMenu } from './Hooks/useUserMenu'

let UserLogin
let UserRegister
let UserInfoMenu

function isAiStudioPage() {
  if (typeof window === 'undefined') return false
  return /^\/ai(\/|$)/.test(window.location.pathname)
}

function UserMenu({
  isCurrentUser = false,
  locale,
  newRoyaltyUi = false,
}) {
  const { userData, handleLogout } = useUserData(isCurrentUser)

  const [isUserLoginLoaded, setIsUserLoginLoaded] = useState(false)
  const [isUserRegisterLoaded, setIsUserRegisterLoaded] = useState(false)
  const [isUserInfoMenuLoaded, setIsUserInfoMenuLoaded] = useState(false)

  function handleWidgetLoad(widgetId, callback) {
    if (widgetId === 'login') {
      if (!UserLogin && !isUserLoginLoaded) {
        import('Pods/Shared/User/Login')
          .then((module) => {
            UserLogin = module.default
            setIsUserLoginLoaded(true)
            callback?.()
          })
          .catch((error) => {
            console.error('Failed to load UserLogin:', error)
          })
      } else {
        // Widget already loaded, just execute callback
        callback?.()
      }
    }

    if (widgetId === 'register') {
      if (!UserRegister && !isUserRegisterLoaded) {
        import('Pods/Shared/User/Register')
          .then((module) => {
            UserRegister = module.default
            setIsUserRegisterLoaded(true)
            callback?.()
          })
          .catch((error) => {
            console.error('Failed to load UserRegister:', error)
          })
      } else {
        // Widget already loaded, just execute callback
        callback?.()
      }
    }

    if (widgetId === 'user-info') {
      if (!UserInfoMenu && !isUserInfoMenuLoaded) {
        import('Pods/LazyLoaded/UserInfoMenu/UserInfoMenu')
          .then((module) => {
            UserInfoMenu = module.default
            setIsUserInfoMenuLoaded(true)
            callback?.()
          })
          .catch((error) => {
            console.error('Failed to load UserInfoMenu:', error)
          })
      } else {
        // Widget already loaded, just execute callback
        callback?.()
      }
    }
  }

  const {
    activeDropdown, setActiveDropdown, wrapperRef,
  } = useUserMenu(isCurrentUser, handleWidgetLoad)

  const isLoaded = {
    login: isUserLoginLoaded && !!UserLogin,
    register: isUserRegisterLoaded && !!UserRegister,
    'user-info': isUserInfoMenuLoaded && !!UserInfoMenu,
  }

  const popoversMap = {
    login: isLoaded.login ? (
      <UserLogin customId="-top-menu-old-layout" captchaId="navbar-captcha" locale={locale} />
    ) : null,
    register: isLoaded.register ? (
      <UserRegister customId="-top-menu-old-layout" source="navigation" captchaId="navbar-captcha" locale={locale} />
    ) : null,
    'user-info': isLoaded['user-info'] ? (
      <UserInfoMenu
        newRoyaltyUi={newRoyaltyUi}
        userData={userData}
        locale={locale}
        closePopover={closePopover}
        handleLogout={handleLogout}
      />
    ) : null,
  }

  function closePopover() {
    setActiveDropdown(false)
  }

  function handlePopoverTrigger(id) {
    // On /ai the auth flow is a dedicated in-page modal (owned by ThreeDStudio),
    // not this header popover. Hand the click off via the global event bus.
    if ((id === 'login' || id === 'register') && isAiStudioPage()) {
      /* eslint-disable-next-line no-undef */
      App.events.publish('app:ai-sign-in:open', { mode: id === 'register' ? 'signup' : 'login' })
      return undefined
    }

    if (activeDropdown === id) {
      return setActiveDropdown(null)
    }

    if (!isLoaded[id]) {
      handleWidgetLoad(id, () => {
        setActiveDropdown(id)
      })
    } else {
      setActiveDropdown(id)
    }
  }

  function handleMouseEnter(id) {
    if (!isLoaded[id]) {
      handleWidgetLoad(id)
    }
  }

  function handleTouchStart(id) {
    if (!isLoaded[id]) {
      handleWidgetLoad(id)
    }
  }

  if (!isCurrentUser) {
    return (
      <div className="wayfinder__user-section" ref={wrapperRef}>
        <Button
          id="user-login-action"
          type="secondary"
          onClick={() => handlePopoverTrigger('login')}
          onMouseEnter={() => handleMouseEnter('login')}
          onTouchStart={() => handleTouchStart('login')}
        >
          {i18n.t('navigation.user.login', { ns: 'rails', lng: locale })}
        </Button>
        <Button
          id="user-register-action"
          customClass="blue-button"
          onClick={() => handlePopoverTrigger('register')}
          onMouseEnter={() => handleMouseEnter('register')}
          onTouchStart={() => handleTouchStart('register')}
        >
          {i18n.t('navigation.user.register', { ns: 'rails', lng: locale })}
        </Button>

        {activeDropdown && (
          <div className="popover is-active">
            <div className="popover-container">
              <div className="popover-content wayfinder__auth-tab">
                {popoversMap[activeDropdown]}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="wayfinder__user-section" ref={wrapperRef}>
      <Avatar
        className="wayfinder__menu-avatar"
        username={userData?.attributes?.username}
        img={userData?.attributes?.avatar?.large_url}
        isClickable
        useSingleLetter
        onClick={() => handlePopoverTrigger('user-info')}
        onMouseEnter={() => handleMouseEnter('user-info')}
        onTouchStart={() => handleTouchStart('user-info')}
      />

      {activeDropdown && (
        <div className="popover popover--narrow is-active">
          <div className="popover-container">
            {popoversMap[activeDropdown]}
          </div>
        </div>
      )}
    </div>
  )
}

UserMenu.propTypes = {
  isCurrentUser: bool,
  locale: string.isRequired,
  newRoyaltyUi: bool,
}

export default UserMenu
